"use client";

import { useEffect, useRef } from "react";
import gsap from "gsap";

/* ── Floating particles background ── */
export function Particles() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Static particles for reduced-motion users: draw one frame, no loop.
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let raf: number;
    const particles: { x: number; y: number; vx: number; vy: number; r: number; o: number }[] = [];

    function resize() {
      // Fresh DPR each time (zoom / monitor moves change it) with an absolute
      // transform — ctx.scale would compound across resizes. Cap at 2:
      // retina x3 buys nothing at 0.1 opacity.
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas!.width = window.innerWidth * dpr;
      canvas!.height = window.innerHeight * dpr;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function init() {
      resize();
      particles.length = 0;
      const count = Math.floor(window.innerWidth / 25);
      for (let i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * window.innerWidth,
          y: Math.random() * window.innerHeight * 3,
          vx: (Math.random() - 0.5) * 0.15,
          vy: -0.1 - Math.random() * 0.2,
          r: 0.5 + Math.random() * 1.2,
          o: 0.08 + Math.random() * 0.15,
        });
      }
    }

    function drawFrame(advance: boolean) {
      ctx!.clearRect(0, 0, window.innerWidth, window.innerHeight);
      const scrollY = window.scrollY;
      for (const p of particles) {
        const screenY = ((p.y - scrollY * 0.05) % (window.innerHeight * 1.5));
        const drawY = screenY < 0 ? screenY + window.innerHeight * 1.5 : screenY;
        ctx!.beginPath();
        ctx!.arc(p.x, drawY, p.r, 0, Math.PI * 2);
        ctx!.fillStyle = `rgba(255, 51, 102, ${p.o})`;
        ctx!.fill();
        if (advance) {
          p.x += p.vx;
          p.y += p.vy;
          if (p.x < 0) p.x = window.innerWidth;
          if (p.x > window.innerWidth) p.x = 0;
        }
      }
    }

    function loop() {
      drawFrame(true);
      raf = requestAnimationFrame(loop);
    }

    // Pause the loop entirely while the tab is hidden — zero CPU cost.
    function onVisibility() {
      if (prefersReduced) return;
      cancelAnimationFrame(raf);
      if (document.visibilityState === "visible") loop();
    }

    // Mobile URL-bar collapse fires height-only resizes — re-seeding there
    // teleports every particle. Only re-seed when the width actually changes.
    let lastWidth = window.innerWidth;
    function onResize() {
      if (window.innerWidth !== lastWidth) {
        lastWidth = window.innerWidth;
        init();
      } else {
        resize();
      }
      if (prefersReduced) drawFrame(false);
    }

    init();
    if (prefersReduced) {
      drawFrame(false); // one static frame keeps the aesthetic
    } else {
      loop();
      document.addEventListener("visibilitychange", onVisibility);
    }
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="fixed inset-0 pointer-events-none z-[1]"
      style={{ width: "100%", height: "100%" }}
    />
  );
}

/* ── Scroll progress bar ── */
export function ScrollProgress() {
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let ticking = false;
    let raf = 0;
    function update() {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const pct = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      if (barRef.current) barRef.current.style.width = `${pct}%`;
      ticking = false;
    }
    // rAF-throttle: one style write per frame instead of per scroll event
    function onScroll() {
      if (!ticking) {
        ticking = true;
        raf = requestAnimationFrame(update);
      }
    }
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <div aria-hidden="true" className="fixed top-0 left-0 right-0 h-[2px] z-[60]">
      <div ref={barRef} className="h-full bg-gradient-to-r from-[#FF3366] to-[#FF9F1C]" style={{ width: "0%" }} />
    </div>
  );
}

/* ── Mouse-glow card wrapper ── */
export function GlowCard({ children, className = "", color = "#FF3366", style }: { children: React.ReactNode; className?: string; color?: string; style?: React.CSSProperties }) {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    function onMove(e: MouseEvent) {
      const rect = card!.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      card!.style.setProperty("--glow-x", `${x}px`);
      card!.style.setProperty("--glow-y", `${y}px`);
    }
    card.addEventListener("mousemove", onMove);
    return () => card.removeEventListener("mousemove", onMove);
  }, []);

  return (
    // `group` + group-hover: the overlay itself is pointer-events-none so it
    // can never match :hover — the hover state must come from the wrapper.
    <div ref={cardRef} className={`group relative overflow-hidden ${className}`} style={style}>
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{ background: `radial-gradient(400px circle at var(--glow-x, 50%) var(--glow-y, 50%), ${color}12, transparent 70%)` }}
      />
      {children}
    </div>
  );
}

/* ── Magnetic button ── */
export function MagneticButton({ children, className = "", href, target, rel, title }: { children: React.ReactNode; className?: string; href: string; target?: string; rel?: string; title?: string }) {
  const ref = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Magnetic pull only makes sense with a fine pointer — on touch it causes
    // the button to jump under the finger; skip for reduced-motion users too.
    const finePointer = window.matchMedia("(pointer: fine)").matches;
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!finePointer || prefersReduced) return;

    function onMove(e: MouseEvent) {
      const rect = el!.getBoundingClientRect();
      const x = (e.clientX - rect.left - rect.width / 2) * 0.15;
      const y = (e.clientY - rect.top - rect.height / 2) * 0.15;
      gsap.to(el, { x, y, duration: 0.3, ease: "power2.out" });
    }
    function onLeave() {
      gsap.to(el, { x: 0, y: 0, duration: 0.5, ease: "elastic.out(1, 0.4)" });
    }
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => { el.removeEventListener("mousemove", onMove); el.removeEventListener("mouseleave", onLeave); };
  }, []);

  return <a ref={ref} href={href} target={target} rel={rel} title={title} className={className}>{children}</a>;
}
